import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactHtmlParser from '@orrisroot/react-html-parser';
import block from 'bem-css-modules';
import trackEvent from 'services/AnalyticsService';
import modalTypes from 'utils/modalTypesEnum';
import styles from './PopupModal.module.scss';

block.setSettings({
  throwOnError: true,
  modifierDelimiter: '--',
});

const bem = block(styles);

const PopupModal = (props) => {
  const {
    parseHtml,
    resultText,
    resultTitle,
    form,
    action,
    modalOk,
    cancelButton,
    cancelAction,
    yesButton,
    cancelTextButton,
    innerCloseButton,
    outerCloseButton,
    bannerCloseButton,
    displayDialogAtTopQuarter,
    displayDialogInMiddle,
    alignBodyCenter,
    modalType,
    roundedEdges,
    headerOverrideStyle,
    backgroundOverrideStyle,
    backgroundButtonMap,
    backgroundButtonAlt,
    linkRef,
    eventTag,
    popupDisplayedEvent,
    icon,
    image,
  } = props;
  let description;
  let title;
  if (parseHtml) {
    description = ReactHtmlParser(resultText);
    title = ReactHtmlParser(resultTitle);
  } else if ((resultText instanceof Array && React.isValidElement(resultText[0])) || resultTitle instanceof Array) {
    description = resultText;
    title = resultTitle;
  } else {
    description = <div align={alignBodyCenter ? 'center' : 'left'}>{resultText}</div>;
    title = resultTitle;
  }

  // Apply colour and width to the close bottom button based on modalType
  const applyBottomButtonColour = (cancel) => {
    if (cancel) {
      return 'button-secondary-grey';
    }

    switch (modalType) {
      case (modalTypes.confirmationModal): return 'button-secondary-grey-confirm';
      case (modalTypes.errorModal):
      case (modalTypes.infoModal): return 'button-primary-orange';
      default: return 'cta-bright-orange-button';
    }
  };

  const applyCancelButtonColour = () => {
    if (modalType === modalTypes.confirmationModal) {
      return 'button-primary-orange-cancel';
    }

    return 'button-secondary-white';
  };

  // Apply additional style to header based on ModalType
  const applyHeaderStyle = () => {
    switch (modalType) {
      case (modalTypes.errorModal):
      case (modalTypes.confirmationModal):
      case (modalTypes.infoModal): return 'info-dialog-header-container';
      default: return null;
    }
  };

  const applyFooterStyle = () => {
    switch (modalType) {
      case (modalTypes.confirmationModal): return 'modal-footer-small';
      default: return 'modal-footer';
    }
  };

  const applyDescriptionStyle = () => {
    if (modalType === modalTypes.infoModal || modalType === modalTypes.errorModal || modalType === modalTypes.confirmationModal) {
      return 'info-dialog-body';
    }
    // eslint-disable-next-line no-use-before-define
    return `${smallOrRegularDialoguePrefix()}--dialog-body`;
  };

  const noMaxHeightPopup = modalType === modalTypes.formModal ? 'no-max-height' : '';
  const borderRadius = roundedEdges ? 'border-radius' : ''

  const smallOrRegularDialoguePrefix = () => {
    switch (modalType) {
      case (modalTypes.smallModal): return 'small-dialog-box';
      default: return 'dialog-box';
    }
  }

  const smallDialogue = () => modalType === modalTypes.smallModal;

  const wideDialoguePrefix = () => {
    switch (modalType) {
      case (modalTypes.wideModal): return 'modal-dialog-wide my-5 mx-auto';
      case (modalTypes.confirmationModal): return 'modal-dialog-confirmation my-5 mx-auto';
      default: return 'modal-dialog';
    }
  }

  const applyModalBackgroundOpacity = () => {
    switch (modalType) {
      case (modalTypes.errorModal):
      case (modalTypes.packageModal):
      case (modalTypes.smallModal):
      case (modalTypes.wideModal):
      case (modalTypes.confirmationModal):
      case (modalTypes.infoModal): return 'info-modal-container';
      default: return 'modal-container';
    }
  };
  const navigate = useNavigate();

  const handleCTA = (e) => {
    e.preventDefault();
    if (eventTag) {
      trackEvent(eventTag);
    }
    navigate(linkRef);
  };

  // Blue circle "X" inside modal window
  const displayInnerCloseButton = () => (
    innerCloseButton && (
      <div className={bem('inner-close-wrapper')}>
        <button
          onClick={cancelAction ?? action}
          data-dismmiss="modal"
          type="button"
          aria-label="Close Modal"
          className="border-0 p-0 bg-white rounded-circle justify-content-end align-self-start ml-auto flex-grow-1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            id="Layer_1"
            width="32"
            height="32"
            x="0"
            y="0"
            version="1.1"
            viewBox="0 0 122.881 122.88"
          >
            <path
              fill="#436A88"
              fillRule="evenodd"
              d="M61.44 0c33.933 0 61.441 27.507 61.441 61.439s-27.508 61.44-61.441 61.44C27.508 122.88 0 95.372 0 61.439S27.508 0 61.44 0m20.279 36.226a3.49 3.49 0 0 1 4.936 4.936l-20.28 20.277 20.279 20.278a3.492 3.492 0 0 1-4.936 4.937L61.44 66.376 41.162 86.654a3.492 3.492 0 0 1-4.936-4.937l20.278-20.278-20.278-20.277a3.49 3.49 0 0 1 4.936-4.936L61.44 56.504z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    )
  );

  // White "X" inside modal overlaying banner background image
  const displayBannerCloseButton = () => (
    bannerCloseButton && (
      <button
        type="button"
        title="Close"
        className="float-right close ascii-cross btn-close btn-close-white fs-5 opacity-100 px-4"
        onClick={action}
        data-dismmiss="modal"
      >
        &nbsp;
      </button>
    )
  );

  // Blue circle "X" positioned outside absolute top right of modal
  const displayOuterCloseButton = () => (
    outerCloseButton && (
      <div className={bem('outer-close-wrapper')}>
        <button
          type="button"
          title="Close"
          className={bem('outer-button')}
          onClick={action}
          data-dismmiss="modal"
        >
          <span className={bem('outer-button-text')}>&#215;</span>
        </button>
      </div>
    )
  );

  useEffect(() => {
    if (popupDisplayedEvent) {
      trackEvent(popupDisplayedEvent);
    }
  }, []);

  return (
    <div className={bem()}>
      <div className={`${bem(applyModalBackgroundOpacity())} modal myrac Modal__overflow`} style={{ zIndex: 1000000050 }} role="dialog">
        <div
          className={`
            ${wideDialoguePrefix()}
            ${bem(smallOrRegularDialoguePrefix())}
            ${bem({
              'dialog-top-quarter': displayDialogAtTopQuarter,
              'dialog-middle': displayDialogInMiddle,
            })}
            ${bem(borderRadius)}
            ${bem(backgroundOverrideStyle && 'banner-container')} Modal__visible
          `}
        >
          <div className={`${bem(!smallDialogue() ? 'modal-content-wrapper' : '')} modal-content`} style={backgroundOverrideStyle}>
            <div className={`${bem('header-container')} ${bem(applyHeaderStyle())}`}>
              {displayInnerCloseButton()}
              {displayOuterCloseButton()}
              {displayBannerCloseButton()}
              {icon && (
                <img src={icon} className="mx-auto d-block" alt={icon} />
              )}
              <div className={`${bem('header-row')}`}>
                <h4 className={`${bem(`${smallOrRegularDialoguePrefix()}--popup-title`)} modal-title Modal__title-white`} style={headerOverrideStyle}>
                  {title}
                </h4>
              </div>
              {image && (
                <img src={image} className="mx-auto px-3 py-4 d-block w-100" alt={image} />
              )}
            </div>
            <div
              className={`${(backgroundButtonMap && handleCTA && bem('popup-no-body')) ?? bem('popup-body')} ${bem(applyDescriptionStyle())} ${bem(noMaxHeightPopup)}`}
            >
              {backgroundButtonMap && handleCTA && (
                <button
                  type="button"
                  style={backgroundButtonMap}
                  onClick={handleCTA}
                  alt={backgroundButtonAlt}
                  className="bg-transparent border-0 d-block"
                >
                  &nbsp;
                </button>
              )}
              {description}
              {form}
            </div>
            <div className={`${bem(applyFooterStyle())}`}>
              <div
                className={`${bem(smallDialogue() ? 'small-dialog-box--button-wrapper' : 'button-wrapper')}`}
              >
                {cancelButton ? (
                  <button
                    className={`${bem(applyBottomButtonColour(true))} MyRacForm__button
                      MyRacForm__button--vertical-spacing
                      MyRacForm__button--wide`}
                    type="button"
                    onClick={cancelAction}
                    data-dismiss="modal"
                  >
                    {cancelButton}
                  </button>
                ) : null}
              </div>
              <div
                className={`${bem(smallDialogue() ? 'small-dialog-box--button-wrapper' : 'button-wrapper')}`}
              >
                {modalOk ? (
                  <button
                    className={`${bem(applyBottomButtonColour())} MyRacForm__button
                      MyRacForm__button--vertical-spacing
                      MyRacForm__button--wide`}
                    type="button"
                    onClick={action}
                    data-dismiss="modal"
                  >
                    {modalOk}
                  </button>
                ) : null}
              </div>
              {yesButton && (
                <button
                  className={`${bem(applyBottomButtonColour())} MyRacForm__button
                    MyRacForm__button--wide
                    w-100 fw-bold`}
                  type="button"
                  onClick={action}
                  data-dismiss="modal"
                >
                  {yesButton}
                </button>
              )}
              {cancelTextButton && (
                <button
                  className={`${bem(applyCancelButtonColour())} MyRacForm__button
                    MyRacForm__button--wide
                    w-100 mt-2`}
                  type="button"
                  onClick={cancelAction}
                  data-dismiss="modal"
                >
                  {cancelTextButton}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade in Modal__block"
        style={{ zIndex: 1000000049 }}
      />
    </div>
  );
};

export default PopupModal;
